import React from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  Typography,
  Box,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { 
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  LocalAtm as LocalAtmIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
    minHeight: '60vh',
    maxWidth: 800,
    margin: theme.spacing(2),
    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
  },
  dialogTitle: {
    padding: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.2)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.3)',
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(3),
  },
  openButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '10px 24px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(34, 109, 104, 0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #1a5a55 0%, #226D68 100%)',
      boxShadow: '0 6px 20px rgba(34, 109, 104, 0.4)',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  titleIcon: {
    fontSize: 28,
  },
}));

// Mapping des icônes par type
const iconMapping = {
  // Icônes par défaut
  default: PersonAddIcon,
  add: AddIcon,
  
  // Gestion des personnes
  person: PersonAddIcon,
  client: PersonAddIcon,
  user: PersonAddIcon,
  
  // Gestion financière
  depense: ReceiptIcon,
  expense: ReceiptIcon,
  payment: PaymentIcon,
  fond: AccountBalanceIcon,
  fund: AccountBalanceIcon,
  money: LocalAtmIcon,
  cash: LocalAtmIcon,
  
  // Gestion des produits
  product: BusinessIcon,
  produit: BusinessIcon,
  
  // Fournisseurs
  fournisseur: BusinessIcon,
  supplier: BusinessIcon,
  vendor: BusinessIcon,
};

export default function CustomizedDialogs({ 
  children, 
  title, 
  boutonTitle, 
  refresh,
  icon = "default", // Nouveau prop pour spécifier l'icône
  customIcon // Nouveau prop pour passer une icône personnalisée
}) {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(false);

  // Sélection de l'icône
  const SelectedIcon = customIcon || iconMapping[icon] || iconMapping.default;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    if (refresh) refresh();
  };

  return (
    <Box>
      <Button 
        variant="contained" 
        className={classes.openButton}
        onClick={handleClickOpen}
        startIcon={<SelectedIcon />}
      >
        {boutonTitle}
      </Button>
      
      <Dialog 
        onClose={handleClose} 
        aria-labelledby="customized-dialog-title" 
        open={open}
        fullScreen={fullScreen}
        classes={{ paper: classes.dialogPaper }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="customized-dialog-title" className={classes.dialogTitle} disableTypography>
          <Box className={classes.titleContainer}>
            <SelectedIcon className={classes.titleIcon} />
            <Typography variant="h5" component="h2" style={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          <IconButton 
            aria-label="close" 
            className={classes.closeButton} 
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent className={classes.dialogContent}>
          {children}
        </DialogContent>
      </Dialog>
    </Box>
  );
}